<template>
<div class="vd-root" :style="themePageStyle">
  <nav class="vd-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:10px;">
      <button class="vd-back" @click="goBack"><i class="mdi mdi-arrow-left"></i></button>
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Find a Vet</span>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-sm btn-outline-warning vd-hdr-btn" @click="$router.push('/vet/dashboard')"><i class="mdi mdi-stethoscope me-1"></i><span class="vd-hdr-label">I'm a Vet</span></button>
      <button class="btn btn-sm btn-warning vd-hdr-btn" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline me-1"></i><span class="vd-hdr-label">Marketplace</span></button>
    </div>
  </div></nav>

  <div class="container vd-container">
    <!-- Hero banner -->
    <div class="vd-hero">
      <div class="vd-hero-icon"><i class="mdi mdi-stethoscope"></i></div>
      <div class="vd-hero-body">
        <h4>Veterinary Services</h4>
        <p>Find KVB-registered vets near you for farm visits, telemedicine, or emergencies.</p>
      </div>
      <button class="vd-hero-cta" @click="$router.push('/vet/dashboard')"><i class="mdi mdi-stethoscope me-1"></i>I'm a Vet</button>
    </div>

    <!-- Filters -->
    <div class="vd-filters mb-4">
      <div class="row g-2">
        <div class="col-md-4">
          <div class="input-group"><span class="input-group-text vd-addon"><i class="mdi mdi-magnify"></i></span>
          <input v-model="keyword" class="form-control vd-input" placeholder="Search by name or practice..." @keyup.enter="search" /></div>
        </div>
        <div class="col-md-2">
          <select v-model="specialization" class="form-select vd-input" @change="search">
            <option value="">All Specializations</option>
            <option value="large_animals">Large Animals</option>
            <option value="poultry">Poultry</option>
            <option value="small_animals">Small Animals</option>
            <option value="equine">Equine</option>
            <option value="wildlife">Wildlife</option>
          </select>
        </div>
        <div class="col-md-2">
          <select v-model="serviceType" class="form-select vd-input" @change="search">
            <option value="">All Services</option>
            <option value="farm_visit">Farm Visit</option>
            <option value="telemedicine">Telemedicine</option>
            <option value="emergency">Emergency 24/7</option>
          </select>
        </div>
        <div class="col-md-2">
          <select v-model="maxDistance" class="form-select vd-input" @change="search">
            <option value="10">Within 10 km</option>
            <option value="25">Within 25 km</option>
            <option value="50" selected>Within 50 km</option>
            <option value="100">Within 100 km</option>
            <option value="">Any distance</option>
          </select>
        </div>
        <div class="col-md-2">
          <button class="btn btn-warning w-100" @click="search"><i class="mdi mdi-magnify me-1"></i>Search</button>
        </div>
      </div>
    </div>

    <!-- Quick filters -->
    <div class="d-flex gap-2 mb-4 flex-wrap vd-chip-row">
      <button class="vd-chip" :class="{active: serviceType==='emergency'}" @click="serviceType=serviceType==='emergency'?'':'emergency';search()">🚨 Emergency</button>
      <button class="vd-chip" :class="{active: serviceType==='telemedicine'}" @click="serviceType=serviceType==='telemedicine'?'':'telemedicine';search()">📹 Telemedicine</button>
      <button class="vd-chip" :class="{active: serviceType==='farm_visit'}" @click="serviceType=serviceType==='farm_visit'?'':'farm_visit';search()">🚗 Farm Visit</button>
      <button class="vd-chip" :class="{active: specialization==='large_animals'}" @click="specialization=specialization==='large_animals'?'':'large_animals';search()">🐄 Large Animals</button>
      <button class="vd-chip" :class="{active: specialization==='poultry'}" @click="specialization=specialization==='poultry'?'':'poultry';search()">🐔 Poultry</button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div><p class="mt-2" style="color:var(--fm-s,#6B6356);">Finding vets near you...</p></div>

    <!-- Empty -->
    <div v-else-if="!vets.length" class="text-center py-5">
      <i class="mdi mdi-stethoscope" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No Vets Found</h5>
      <p style="color:var(--fm-s,#6B6356);">Try broadening your search or increasing the distance range.</p>
    </div>

    <!-- Results -->
    <div v-else>
      <p class="mb-3" style="color:var(--fm-s,#6B6356);font-size:13px;">{{ total }} vets found</p>
      <div class="row g-3">
        <div class="col-md-6 col-lg-4" v-for="vet in vets" :key="vet.id">
          <div class="vd-card" @click="$router.push('/vet/'+vet.user_id)">
            <div class="d-flex align-items-center gap-3 mb-3">
              <user-avatar :src="vet.avatar_url" :name="vet.display_name||vet.vet_name" size="md" :ring="vet.kvb_verified ? 'gold' : ''" :badge="vet.kvb_verified ? 'vet' : ''" />
              <div class="flex-fill" style="min-width:0;">
                <div class="vd-name">{{ vet.display_name || vet.vet_name }}</div>
                <div class="vd-practice" v-if="vet.practice_name">{{ vet.practice_name }}</div>
                <div class="vd-kvb" v-if="vet.kvb_verified"><i class="mdi mdi-check-decagram me-1" style="color:#50C878;"></i>KVB Verified</div>
              </div>
              <div v-if="vet.avg_rating" class="text-end">
                <div style="color:#FFD700;font-size:14px;"><i class="mdi mdi-star"></i> {{ Number(vet.avg_rating).toFixed(1) }}</div>
                <div style="color:var(--fm-s,#6B6356);font-size:10px;">{{ vet.total_reviews }} reviews</div>
              </div>
            </div>

            <div class="d-flex flex-wrap gap-1 mb-3">
              <span v-if="vet.is_mobile" class="vd-tag vd-tag-green"><i class="mdi mdi-car me-1"></i>Farm Visit</span>
              <span v-if="vet.offers_telemedicine" class="vd-tag vd-tag-blue"><i class="mdi mdi-video me-1"></i>Telemedicine</span>
              <span v-if="vet.offers_emergency" class="vd-tag vd-tag-red"><i class="mdi mdi-alarm-light me-1"></i>Emergency</span>
            </div>

            <div v-if="vet.specializations && vet.specializations.length" class="mb-3" style="font-size:11px;color:var(--fm-m,#A09888);">
              <i class="mdi mdi-tag-multiple me-1"></i>{{ (vet.specializations||[]).map(function(s){return s.replace(/_/g,' ')}).join(', ') }}
            </div>

            <div class="d-flex justify-content-between align-items-center" style="border-top:1px solid var(--fm-br,#1E1E1E);padding-top:10px;">
              <div>
                <div v-if="vet.consultation_fee" style="color:#BFA164;font-weight:600;font-size:13px;">KES {{ Number(vet.consultation_fee).toLocaleString() }}</div>
                <div style="color:var(--fm-s,#6B6356);font-size:10px;">consultation fee</div>
              </div>
              <div v-if="vet.distance_km" style="color:var(--fm-s,#6B6356);font-size:12px;">
                <i class="mdi mdi-map-marker me-1"></i>{{ Number(vet.distance_km).toFixed(1) }} km
              </div>
              <div v-else-if="vet.clinic_address" style="color:var(--fm-s,#6B6356);font-size:11px;max-width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
                <i class="mdi mdi-map-marker me-1"></i>{{ vet.clinic_address }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="page < pages" class="text-center mt-4">
        <button class="btn btn-outline-warning" @click="loadMore" :disabled="loadingMore">
          <span v-if="loadingMore"><span class="spinner-border spinner-border-sm me-1"></span></span>Load More
        </button>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#EBE7E1;--fm-bg3:#E9E4DC;--fm-br:#E2DCD3}
html[data-theme="light"] body{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#EBE7E1!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#EBE7E1!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E2DCD3!important}
html[data-theme="light"] .vd-root{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] .vd-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E2DCD3!important}
html[data-theme="light"] .vd-card{background:#FFFFFF!important;border-color:#E2DCD3!important}
html[data-theme="light"] .vd-input,.vd-addon{background:#FFFFFF!important;border-color:#E2DCD3!important;color:#1A1A1A!important}
html[data-theme="light"] .vd-chip{background:#FFFFFF!important;border-color:#E2DCD3!important;color:#555!important}
html[data-theme="light"] .vd-chip.active{background:rgba(191,161,100,.1)!important;border-color:#BFA164!important;color:#BFA164!important}
html[data-theme="light"] .vd-hero{background:linear-gradient(120deg,#e6f0e6,#f3efe7)!important;border-color:#E2DCD3!important}
html[data-theme="light"] .vd-hero-body h4{color:#1A1A1A!important}
html[data-theme="light"] .vd-hero-body p{color:#555!important}
</style>

<style scoped>
.vd-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.vd-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.vd-back{width:36px;height:36px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:19px}
.vd-card{border-radius:14px!important}
.vd-hero{display:flex;align-items:center;gap:16px;background:linear-gradient(120deg,#1d2a1d,#14110a);border:1px solid var(--fm-br,#1E1E1E);border-radius:16px;padding:18px 20px;margin-bottom:20px}
.vd-hero-icon{width:56px;height:56px;border-radius:14px;background:rgba(80,200,120,.15);color:#50C878;display:flex;align-items:center;justify-content:center;font-size:30px;flex-shrink:0}
.vd-hero-body{flex:1;min-width:0}
.vd-hero-body h4{color:#F5F0E8;font-weight:800;margin:0 0 3px;font-size:20px}
.vd-hero-body p{color:#C9C3B8;font-size:13px;margin:0;line-height:1.5}
.vd-hero-cta{background:#BFA164;border:none;color:#0A0A0A;font-weight:700;font-size:13px;padding:9px 16px;border-radius:9px;white-space:nowrap;flex-shrink:0}
@media (max-width:600px){.vd-hero-cta{display:none}.vd-hero-body p{display:none}}
.vd-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.vd-input{background:var(--fm-bg1,#141414)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.vd-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}
.vd-addon{background:var(--fm-bg2,#0F0F0F)!important;border-color:var(--fm-br,#1E1E1E)!important;color:var(--fm-s,#6B6356)!important}
.vd-chip{padding:6px 14px;border-radius:20px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg1,#141414);color:var(--fm-m,#A09888);font-size:12px;cursor:pointer;transition:all .2s}
.vd-chip:hover{border-color:#BFA164;color:#BFA164}
.vd-chip.active{background:rgba(191,161,100,.1);border-color:#BFA164;color:#BFA164;font-weight:600}
.vd-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:20px;cursor:pointer;transition:all .2s}
.vd-card:hover{border-color:#BFA164;transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.2)}
.vd-avatar{width:50px;height:50px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:700;color:#BFA164;overflow:hidden;flex-shrink:0}
.vd-avatar img{width:100%;height:100%;object-fit:cover}
.vd-name{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:14px}
.vd-practice{color:var(--fm-m,#A09888);font-size:11px}
.vd-kvb{color:var(--fm-s,#6B6356);font-size:10px;margin-top:2px}
.vd-tag{font-size:10px;padding:2px 8px;border-radius:6px;font-weight:600}
.vd-tag-green{background:rgba(80,200,120,.1);color:#50C878}
.vd-tag-blue{background:rgba(79,195,247,.1);color:#4FC3F7}
.vd-tag-red{background:rgba(255,107,107,.1);color:#FF6B6B}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  /* Header buttons: icon-only pills so the title fits on one line */
  .vd-hdr-label { display: none; }
  .vd-hdr-btn { padding: 4px 9px; }

  /* Hero: hide entirely on mobile — the chip row + filters cover the same job */
  .vd-hero { display: none; }

  /* Container padding tighter on mobile */
  .vd-container { padding-top: 70px; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)); padding-left: 12px; padding-right: 12px; }

  /* Compact dropdowns + search button */
  .vd-input { font-size: 12px; padding: 6px 10px; }
  .vd-addon { padding: 6px 10px; }
  .vd-filters .row { row-gap: 6px !important; }
  .vd-filters .btn-warning { padding: 6px 10px; font-size: 12px; }

  /* Chip row: single horizontal scroller instead of wrapping rows */
  .vd-chip-row { flex-wrap: nowrap !important; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; padding-bottom: 4px; margin-bottom: 12px !important; }
  .vd-chip-row::-webkit-scrollbar { display: none; }
  .vd-chip { flex-shrink: 0; padding: 5px 12px; font-size: 11px; }

  /* Cards tighter */
  .vd-card { padding: 12px !important; border-radius: 10px !important; }
  .vd-avatar { width: 42px; height: 42px; font-size: 14px; }
  .vd-name { font-size: 13px; }
  .vd-tag { font-size: 9px; padding: 2px 6px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";
export default {
  name: "VetDirectory",
  components: { UserAvatar },
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,
    loading:false,loadingMore:false,
    keyword:'',specialization:'',serviceType:'',maxDistance:'50',
    vets:[],total:0,page:1,pages:1,
    userLat:null,userLng:null
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#EBE7E1;min-height:100vh;color:#1A1A1A;'}
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    this.darkMode=t!=='light';
    this.syncTheme();
    this.themeObserver=new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.getUserLocation();
    this.search();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect()},
  methods:{
    goBack(){ if(window.history.length>1) this.$router.go(-1); else this.$router.push('/forum'); },
    syncTheme(){this.darkMode=document.documentElement.getAttribute('data-theme')!=='light'},
    getUserLocation(){
      if(navigator.geolocation){
        navigator.geolocation.getCurrentPosition(
          function(pos){this.userLat=pos.coords.latitude;this.userLng=pos.coords.longitude;this.search()}.bind(this),
          function(){this.search()}.bind(this),{timeout:5000}
        );
      }
    },
    async search(){
      this.loading=true;this.page=1;this.vets=[];
      try{
        var params={function:'searchVets',keyword:this.keyword,specialization:this.specialization};
        if(this.serviceType==='telemedicine')params.offersTelemedicine='1';
        if(this.serviceType==='emergency')params.offersEmergency='1';
        if(this.userLat){params.latitude=this.userLat;params.longitude=this.userLng}
        var r=await executeJson(params,flocker);
        if(r.data&&r.data.success){
          this.vets=r.data.data.vets||[];
          this.total=r.data.data.total||this.vets.length;
          this.pages=r.data.data.pages||1;
          // Parse JSON fields if needed
          this.vets.forEach(function(v){
            if(typeof v.specializations==='string')try{v.specializations=JSON.parse(v.specializations)}catch(e){v.specializations=[]}
            if(typeof v.services_offered==='string')try{v.services_offered=JSON.parse(v.services_offered)}catch(e){v.services_offered=[]}
          });
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    async loadMore(){
      this.loadingMore=true;this.page++;
      try{
        var params={function:'searchVets',keyword:this.keyword,specialization:this.specialization,page:this.page};
        if(this.serviceType==='telemedicine')params.offersTelemedicine='1';
        if(this.serviceType==='emergency')params.offersEmergency='1';
        if(this.userLat){params.latitude=this.userLat;params.longitude=this.userLng}
        var r=await executeJson(params,flocker);
        if(r.data&&r.data.success){
          var newVets=r.data.data.vets||[];
          newVets.forEach(function(v){
            if(typeof v.specializations==='string')try{v.specializations=JSON.parse(v.specializations)}catch(e){v.specializations=[]}
          });
          this.vets=this.vets.concat(newVets);
        }
      }catch(e){console.error(e)}
      this.loadingMore=false;
    }
  }
};
</script>
